import * as React from 'react';
import { useEffect, useState } from "react";
import { Chart } from 'chart.js';
import { SiteVariablesPrepared } from "@fluentui/react-northstar";
import { IChartData } from "../chart-types";
import { tooltipTrigger, chartConfig, axesConfig, setTooltipColorScheme, numberFormat, pieChartConfig } from "../chart-utils";
import { ChartContainer } from "./chart-container";
import { buildPattern, chartBarDataPointPatterns } from "../chart-patterns";
import { getText } from "../../../translations";

const DoughnutChart = ({
    title,
    data,
    siteVariables,
    cutoutPercentage,
    custom,
    borderWidth,
    collapsed,
    theme,
    customColors,
    activeSegmentIndex,
    onChangeSegmentIndex,
}: {
    title: string;
    data: IChartData;
    siteVariables: SiteVariablesPrepared;
    cutoutPercentage?: number;
    custom?: boolean;
    borderWidth?: number;
    customWidth?: number;
    customHeight?: number;
    collapsed?: boolean;
    theme?: string;
    customColors?: string[];
    activeSegmentIndex?: number;
    onChangeSegmentIndex?: Function;
}) => {
    if (data && data.datasets && data.datasets[0].data.length > 6) {
        data.datasets[0].data = data.datasets[0].data.slice(0, 6);
        console.warn(
            "Please follow design guidence and apply 6 or less data points per one chart."
        );
    }
    const { colorScheme, colors, t } = siteVariables;
    const canvasRef = React.useRef<HTMLCanvasElement | null>(null);
    const chartRef = React.useRef<Chart | undefined>();
    const chartId = React.useMemo(
        () => Math.random().toString(36).substr(2, 9),
        []
    );

    const chartDataPointColors = React.useMemo(
        () => customColors ? customColors : [
            '#27438C',
            '#D8D8D8 ',
        ],
        [theme]
    );

    const chartDataPointColorsDark = React.useMemo(
        () => customColors ? customColors : [
            '#508BD5',
            '#484644',
        ],
        [theme]
    );

    const createDataPoints = (): Chart.ChartDataSets[] => {

        let dataPointConfig = {
            label: getText(t?.locale, data.datasets[0].label),
            data: data.datasets[0].data,
            borderWidth: borderWidth ? borderWidth : 0,
            borderColor: colorScheme.default.background,
            hoverBorderColor: colorScheme.default.background,
            backgroundColor: chartDataPointColors,
            hoverBackgroundColor: chartDataPointColors,
        };

        if (theme === "dark") {
            dataPointConfig = {
                label: getText(t?.locale, data.datasets[0].label),
                data: data.datasets[0].data,
                borderWidth: 0,
                borderColor: colorScheme.default.background,
                hoverBorderColor: colorScheme.default.background,
                backgroundColor: chartDataPointColorsDark,
                hoverBackgroundColor: chartDataPointColorsDark,
            };
        }

        return [dataPointConfig] as any;
    };

    useEffect(() => {
        if (!canvasRef.current) return;
        const ctx = canvasRef.current.getContext("2d");
        if (!ctx) return;

        const config: any = pieChartConfig();
        config.options.hover.mode = "point";

        config.options.layout.padding.top = 32;
        config.options.layout.padding.left = -16;
        config.options.layout.padding.right = 32;
        config.options.layout.padding.bottom = 32;

        config.options.scales.xAxes[0].ticks.display = false;
        config.options.scales.xAxes[0].gridLines.display = false;

        config.options.scales.yAxes[0].ticks.display = false;
        config.options.scales.yAxes[0].gridLines.display = false;

        if (cutoutPercentage) {
            config.options.cutoutPercentage = cutoutPercentage;
        }
        // Pie chart custom settings
        config.options.tooltips.callbacks.label = (tooltipItem: any, data: any) =>
            getText(t?.locale, data.labels[tooltipItem.index]);
        config.options.tooltips.callbacks.labelColor = (tooltipItem: any) => ({
            backgroundColor: chartDataPointColors[tooltipItem.index],
        });

        config.options.tooltips.callbacks.title = (tooltipItems: any) => {
            return `${(
                (Number(data.datasets[0].data[tooltipItems[0].index]) /
                    (data.datasets[0].data as number[]).reduce((a, b) => a + b)) *
                100
            ).toPrecision(2)}% (${numberFormat(
                Number(data.datasets[0].data[tooltipItems[0].index])
            )})`;
        };

        const chart: any = chartRef.current;

        const doughnutLabelsLine = {
            id: 'doughnutLablesLine',
            afterDraw(chart, args, options) {
                const { ctx, chartArea: { top, bottom, left, right, width, height } } = chart;

                chart.data.datasets.forEach((dataset, i) => {
                    chart.getDatasetMeta(i).data.forEach((datapoint, index) => {
                        var updatedColors = activeSegmentIndex === index ? chartDataPointColors[index] : "#f5f5f5";

                        if (activeSegmentIndex == null)
                            updatedColors = chartDataPointColors[index];

                        const { x, y } = datapoint.tooltipPosition();

                        // draw line
                        const halfWidth = datapoint._view.x;
                        const halfHeight = 100;

                        const xLine = x >= halfWidth ? x + 5 : x - 15;
                        const yLine = y >= halfHeight ? y + 5 : y - 15;
                        const extraLine = x >= halfWidth ? 15 : -15;

                        // line
                        ctx.beginPath();
                        ctx.moveTo(x, y);
                        ctx.lineTo(xLine, yLine);
                        ctx.lineTo(xLine + extraLine, yLine);
                        ctx.strokeStyle = updatedColors;
                        ctx.stroke();

                        // text
                        const textWidth = ctx.measureText(chart.data.labels[index]).width;
                        ctx.font = '12px Arial';

                        const textXPosition = x >= halfWidth ? 'left' : 'right';

                        const percent = (
                            (Number(data.datasets[0].data[index]) /
                                (data.datasets[0].data as number[]).reduce((a, b) => a + b)) *
                            100
                        ).toFixed(2); 

                        ctx.textAlign = textXPosition;
                        ctx.textBaseLine = 'middle';
                        ctx.fillStyle = updatedColors;
                        ctx.fillText(chart.data.labels[index], xLine + extraLine, yLine);
                        ctx.fillText(`${percent}%`, xLine + extraLine, yLine + 15);
                    })
                })
            },
        }
       
        var optionsConfig = !custom ?
            {
                responsive: true,
                maintainAspectRatio: false,
                circumference: Math.PI,
                rotation: Math.PI,
                cutoutPercentage: cutoutPercentage ? cutoutPercentage : 70,
                animation: {
                    animateScale: true,
                    animateRotate: true
                },
                title: {
                    display: true,
                    text: '',
                    position: 'bottom'
                }
            } :
            {
                aspectRatio: 1.65,
                responsive: true,
                maintainAspectRatio: false,
                layout: {
                    padding: {
                        left: 10,
                        right: 10,
                        top: 25,
                        bottom: 0
                    }
                },
                plugins: {
                    legend: {
                        display: false,
                    }
                },
                cutoutPercentage: cutoutPercentage ? cutoutPercentage : 70,
                animation: {
                    animateScale: false,
                    animateRotate: false,
                },
                title: {
                    display: true,
                    text: '',
                    position: 'bottom'
                },
                onHover: (event, chartElement) => {
                    event.target.style.cursor = chartElement[0] ? 'pointer' : 'default';
                },
                onClick: (e, items) => {
                    if (items.length === 0) return;
                    const clickedIndex = items[0]._index;

                    if (activeSegmentIndex == clickedIndex) {
                        onChangeSegmentIndex(null);
                    } else {
                        onChangeSegmentIndex(clickedIndex);
                    }
                }
            }

        chartRef.current = new Chart(ctx, {
            ...(config as any),
            data: {
                labels: Array.isArray(data.labels)
                    ? data.labels.map((label) => getText(t?.locale, label).split(" (")[0])
                    : getText(t?.locale, data.labels),
                datasets: [],
            },
            options: optionsConfig,
            plugins: !custom ? [] : [doughnutLabelsLine],
        });

        return () => {
            if (!chartRef.current) return;

            chartRef.current.destroy();
        };
    }, [data, activeSegmentIndex]);

    useEffect(() => {
        if (!chartRef.current) return;
        if (!canvasRef.current) return;
        const ctx = canvasRef.current.getContext("2d");
        if (!ctx) return;

        let updatedColors = data.datasets[0].data.map((_, index) =>
            activeSegmentIndex === index ? chartDataPointColors[index] : "#f5f5f5"
        );

        if (activeSegmentIndex == null)
            updatedColors = chartDataPointColors;

        chartRef.current.data.datasets = [
            {
                label: getText(t?.locale, data.datasets[0].label),
                data: data.datasets[0].data,
                borderWidth: borderWidth ? borderWidth : 0,
                borderColor: colorScheme.default.background,
                hoverBorderColor: colorScheme.default.background,
                backgroundColor: updatedColors,
                hoverBackgroundColor: updatedColors,
            },
        ];

        if (activeSegmentIndex != null) {
            chartRef.current.options.animation.animateScale = false;
            chartRef.current.options.animation.animateRotate = false;
        }

        chartRef.current.update();
    }, [data, activeSegmentIndex]);


    /**
     * Theme updates
     */
    useEffect(() => {
        if (!chartRef.current) return;
        if (!canvasRef.current) return;
        const ctx = canvasRef.current.getContext("2d");
        if (!ctx) return;
        // Apply new colors scheme for data points
        activeSegmentIndex == null ? chartRef.current.data.datasets = createDataPoints() : null;
        // Update tooltip colors scheme
        setTooltipColorScheme({
            chart: chartRef.current,
            siteVariables,
            chartDataPointColors,
            patterns: chartBarDataPointPatterns,
            verticalDataAlignment: true,
        });
        // Update axeses
        axesConfig({ chart: chartRef.current, ctx, colorScheme });

        //onChangeSegmentIndex(null);

        chartRef.current.update();
    }, [theme, data, activeSegmentIndex]);

    function onLegendClick(datasetIndex: number) {
        if (!chartRef.current) return;
        chartRef.current.update();
    }

    return (
        <ChartContainer
            siteVariables={siteVariables}
            data={data}
            chartDataPointColors={chartDataPointColors}
            patterns={chartBarDataPointPatterns}
            onLegendClick={onLegendClick}
            verticalDataAlignment
            hideLegend={collapsed}
        >
            <canvas
                id={chartId}
                ref={canvasRef}
                tabIndex={0}
                style={{
                    userSelect: "none",
                }}
                aria-label={title}
            >
                {data.datasets.map((set, setKey) =>
                    (set.data as number[]).forEach((item: number, itemKey: number) => (
                        // Generated tooltips for screen readers
                        <div key={itemKey} id={`${chartId}-tooltip-${setKey}-${itemKey}`}>
                            <p>{item}</p>
                            <span>
                                {getText(t?.locale, set.label)}: {set.data[itemKey]}
                            </span>
                        </div>
                    ))
                )}
            </canvas>
        </ChartContainer>
    );
};

export default DoughnutChart;
